/**
 * Load Balance Board — derivations.
 *
 * Everything the board renders is computed from the week's assignments on each
 * render; nothing here is stored. A manual move is saved on the server the
 * moment it is made and the assignments are refetched, so the board only ever
 * draws what is actually planned.
 */
import { addDays, differenceInCalendarDays, parseISO, startOfDay } from "date-fns";

import { AvailabilityRow, RosterAssignment } from "../../../../../services/roster-assignments-api";
import { ShiftInstance } from "../../../../../services/shifts-api";
import { ManufacturingOrder } from "../../../production-plan/utils/calculations";
import { dayStartMs, hoursInto, localDay } from "../../../utils/time";
import { AvailabilityIndex, isConfirmedRow } from "./availability";
import { Band, bandFor,DEFAULT_CAP_HOURS } from "./tokens"; // eslint-disable-line sort-imports

export interface BoardBlock {
	id: number
	label: string
	/** Decimal hours into the day, e.g. 7.5 = 07:30. */
	start: number
	end: number
	duration: number
	jobRole: string
	workOrderId: number
	workerId: string
	workerName: string
	phaseId: number | null
	segmentSeq: number | null
	/** The work center of the step, when the server sent it — for the standing-pin lookup. */
	workstation: string | null
	/** Placed by hand (or moved) and pinned — Allocate leaves it alone. */
	isManuallyAssigned: boolean
	/** Why the worker is not expected on this block's shift (or asking not to be); null when expected. */
	unavailable: AvailabilityRow | null
	/** Confirmed out for the block's shift — drawn, not counted as cover. */
	isOut: boolean
}

export interface WorkerLoad {
	workerId: string
	name: string
	role: string
	hours: number
	/** This worker's cap for the day — the length of their shift. */
	cap: number
	blocks: BoardBlock[]
	band: Band
	/**
	 * The row that marks the worker's day: a confirmed-out block's, else a
	 * pending request's, else the day-level row when they hold no block.
	 */
	unavailable: AvailabilityRow | null
	/** Hours of this worker's blocks that are confirmed out — short in the coverage. */
	outHours: number
}

export interface WeekLoad {
	workerId: string
	name: string
	role: string
	/** Index 0 = Monday. */
	dayHours: number[]
	/** Per-day caps (shift lengths), index 0 = Monday. */
	dayCaps: number[]
	hours: number
	/** This worker's cap for the week — the sum of their shift lengths. */
	cap: number
	/** The assignments behind each day cell, index 0 = Monday — a single one is draggable. */
	dayAssignments: RosterAssignment[][]
	/** Per day, the availability row that marks the worker out (or requesting), index 0 = Monday. */
	dayUnavailable: (AvailabilityRow | null)[]
}

/**
 * The row that marks a worker on a day: the first confirmed row on any of
 * their shifts that day, else the first pending one, else the day's own row.
 */
function markerFor(
	availability: AvailabilityIndex | undefined,
	workerId: string,
	assignments: RosterAssignment[],
	dayIso: string
): AvailabilityRow | null {
	if (!availability) {
		return null;
	}
	const rows = assignments
		.map((a) => availability.lookup(workerId, a.shiftId ?? null, dayIso))
		.filter((row): row is AvailabilityRow => row !== null);
	return rows.find(isConfirmedRow) ?? rows[0] ?? availability.lookup(workerId, null, dayIso);
}

/**
 * Where a worker's cap comes from. A plain number is the same cap for everyone
 * (tests, last-resort fallback); a resolver answers per worker from the
 * assignments being measured — which is how the cap follows the SHIFT: an
 * 8h 30m shift is an 8h 30m cap, and nobody on it is "over the 8h cap".
 */
export type CapResolver = (workerId: string, assignments: RosterAssignment[]) => number
export type CapInput = number | CapResolver

const resolveCap = (cap: CapInput, workerId: string, assignments: RosterAssignment[]): number =>
	typeof cap === "number" ? cap : cap(workerId, assignments);

/** Scheduled length of a shift instance in hours (end − start; nothing subtracted). */
export const shiftLengthHours = (shift: ShiftInstance): number =>
	Math.max(0, (parseISO(shift.endDateTime).getTime() - parseISO(shift.startDateTime).getTime()) / 3600000);

const overlapsRange = (shift: ShiftInstance, start: Date, end: Date): boolean =>
	parseISO(shift.startDateTime) < end && parseISO(shift.endDateTime) > start;

/**
 * Day cap from the shifts: the worker's own shift that day (matched through the
 * assignment's shiftId), else the longest shift that overlaps the day, else the
 * default. Only the default is a constant.
 */
export function dayShiftCap(shifts: ShiftInstance[], day: Date, fallback = DEFAULT_CAP_HOURS): CapResolver {
	const dayStart = new Date(day);
	dayStart.setHours(0, 0, 0, 0);
	const dayEnd = new Date(dayStart.getTime() + 86400000);
	const byId = new Map(shifts.map((shift) => [shift.shiftId, shift]));
	const longestOnDay = shifts
		.filter((shift) => overlapsRange(shift, dayStart, dayEnd))
		.reduce<number | null>((longest, shift) => Math.max(longest ?? 0, shiftLengthHours(shift)), null);

	return (_workerId, assignments) => {
		const own = assignments
			.map((a) => (a.shiftId ? byId.get(a.shiftId) : undefined))
			.filter((shift): shift is ShiftInstance => Boolean(shift))
			.map(shiftLengthHours);
		if (own.length) {
			return Math.max(...own);
		}
		return longestOnDay && longestOnDay > 0 ? longestOnDay : fallback;
	};
}

/** The seven day-cap resolvers of a week (index 0 = Monday), computed once for every worker's cells. */
export const weekDayCaps = (shifts: ShiftInstance[], weekStart: Date): CapResolver[] =>
	Array.from({ length: 7 }, (_, d) => dayShiftCap(shifts, startOfDay(addDays(weekStart, d))));

/**
 * Week cap from the shifts: the sum of the worker's own shift lengths in the
 * week (each shift once, however many assignments sit on it), else 5 x the
 * longest shift in the week, else 5 x the default.
 */
export function weekShiftCap(shifts: ShiftInstance[], weekStart: Date, fallback = DEFAULT_CAP_HOURS): CapResolver {
	const start = new Date(weekStart);
	start.setHours(0, 0, 0, 0);
	const end = new Date(start.getTime() + 7 * 86400000);
	const byId = new Map(shifts.map((shift) => [shift.shiftId, shift]));
	const longestInWeek = shifts
		.filter((shift) => overlapsRange(shift, start, end))
		.reduce<number | null>((longest, shift) => Math.max(longest ?? 0, shiftLengthHours(shift)), null);

	return (_workerId, assignments) => {
		const ownIds = new Set(assignments.map((a) => a.shiftId).filter((id): id is string => Boolean(id && byId.has(id))));
		if (ownIds.size) {
			return Array.from(ownIds).reduce((total, id) => total + shiftLengthHours(byId.get(id) as ShiftInstance), 0);
		}
		return 5 * (longestInWeek && longestInWeek > 0 ? longestInWeek : fallback);
	};
}

/**
 * Real deployed hours: overlapping assignments are merged, so a worker on two
 * concurrent tasks is not credited twice. Anything else would overstate load
 * on precisely the people the board exists to flag.
 */
export function mergedHours(assignments: RosterAssignment[]): number {
	const spans = assignments
		.map((a) => ({ s: parseISO(a.startTime).getTime(), e: parseISO(a.endTime).getTime() }))
		.filter((span) => span.e > span.s)
		.sort((a, b) => a.s - b.s);

	let total = 0;
	let cursor = -Infinity;
	spans.forEach((span) => {
		const start = Math.max(span.s, cursor);
		if (span.e > start) {
			total += span.e - start;
			cursor = span.e;
		}
	});
	return total / 3600000;
}

/**
 * Every worker who appears anywhere in the week, so somebody who is idle on the
 * selected day still shows as under-loaded rather than vanishing — an empty row
 * is the whole point of a balance board.
 */
function rosterOf(weekAssignments: RosterAssignment[]): Map<string, { name: string; role: string }> {
	const roster = new Map<string, { name: string; role: string }>();
	weekAssignments.forEach((a) => {
		const id = String(a.workerId);
		if (!roster.has(id)) {
			roster.set(id, { name: a.workerName, role: a.jobRoleName || "—" });
		}
	});
	return roster;
}

export function buildDayLoads(
	weekAssignments: RosterAssignment[],
	day: Date,
	cap: CapInput,
	operationNames?: Map<number, string>,
	/** Who is out: marks the blocks and the row, and takes the out hours off the coverage. */
	availability?: AvailabilityIndex
): WorkerLoad[] {
	const roster = rosterOf(weekAssignments);
	const dayIso = localDay(day);
	// Assignments that OVERLAP the day, not only those that start on it, so the
	// tail of a step that crossed midnight loads the worker on day 2. The part
	// outside the day is clipped off, so hours and blocks are the day's share.
	const dayStart = new Date(dayStartMs(day));
	const dayEnd = new Date(dayStart.getTime() + 86400000);
	const dayAssignments = weekAssignments
		.filter((a) => parseISO(a.startTime) < dayEnd && parseISO(a.endTime) > dayStart)
		.map((a) => ({
			...a,
			startTime: new Date(Math.max(parseISO(a.startTime).getTime(), dayStart.getTime())).toISOString(),
			endTime: new Date(Math.min(parseISO(a.endTime).getTime(), dayEnd.getTime())).toISOString(),
		}));

	const byWorker = new Map<string, RosterAssignment[]>();
	roster.forEach((_, id) => byWorker.set(id, []));
	dayAssignments.forEach((a) => {
		const owner = String(a.workerId);
		byWorker.set(owner, [...(byWorker.get(owner) || []), a]);
	});

	const loads: WorkerLoad[] = [];
	byWorker.forEach((assignments, workerId) => {
		const who = roster.get(workerId);
		if (!who) {
			return;
		}
		const hours = mergedHours(assignments);
		const workerCap = resolveCap(cap, workerId, assignments);
		// A worker's role on the board is the work they actually did today, so a
		// cross-skilled person reads "Electrical · Mechanical" rather than one label.
		const rolesToday = Array.from(new Set(assignments.map((a) => a.jobRoleName).filter(Boolean)));
		const blocks = assignments
			.map((a) => {
				const unavailable = availability?.lookup(workerId, a.shiftId ?? null, dayIso) ?? null;
				return {
					id: a.id,
					label: `${
						(a.laborStandardPhaseId && operationNames?.get(a.laborStandardPhaseId)) ||
						a.jobRoleName ||
						"Assignment"
					}${a.segmentSeq != null ? ` · part ${a.segmentSeq}` : ""}`,
					start: hoursInto(a.startTime, dayStart.getTime()),
					end: hoursInto(a.endTime, dayStart.getTime()),
					duration: (parseISO(a.endTime).getTime() - parseISO(a.startTime).getTime()) / 3600000,
					jobRole: a.jobRoleName,
					workOrderId: a.workOrderId,
					workerId: String(a.workerId),
					workerName: a.workerName,
					phaseId: a.laborStandardPhaseId ?? null,
					segmentSeq: a.segmentSeq ?? null,
					workstation: a.workstation ?? null,
					isManuallyAssigned: Boolean(a.isManuallyAssigned),
					unavailable,
					isOut: unavailable !== null && isConfirmedRow(unavailable),
				};
			})
			.sort((x, y) => x.start - y.start);
		loads.push({
			workerId,
			name: who.name,
			role: rolesToday.length ? rolesToday.sort().join(" · ") : who.role,
			hours,
			cap: workerCap,
			band: bandFor(hours, workerCap),
			blocks,
			unavailable: markerFor(availability, workerId, assignments, dayIso),
			outHours: mergedHours(assignments.filter((a) => blocks.find((block) => block.id === a.id)?.isOut)),
		});
	});

	return loads.sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name));
}

export function buildWeekLoads(
	weekAssignments: RosterAssignment[],
	weekStart: Date,
	cap: CapInput = DEFAULT_CAP_HOURS * 5,
	/**
	 * Per-day cap resolvers for the day cells, index 0 = Monday (see
	 * `weekDayCaps`); defaults to a fifth of the week cap. Built once by the
	 * caller, not once per worker per day.
	 */
	dayCapResolvers?: CapResolver[],
	/** Who is out, for the day cells' markers. */
	availability?: AvailabilityIndex
): WeekLoad[] {
	const roster = rosterOf(weekAssignments);
	const dayIsos = Array.from({ length: 7 }, (_, d) => localDay(addDays(weekStart, d)));
	const perWorkerPerDay = new Map<string, RosterAssignment[][]>();
	roster.forEach((_, id) =>
		perWorkerPerDay.set(
			id,
			Array.from({ length: 7 }, () => [] as RosterAssignment[])
		)
	);

	weekAssignments.forEach((a) => {
		const buckets = perWorkerPerDay.get(String(a.workerId));
		if (!buckets) {
			return;
		}
		// Calendar days, not ms / 86 400 000: a DST week is 23 or 25 hours short
		// or long somewhere, and the division would put Sunday in Saturday's bucket.
		const dayIndex = differenceInCalendarDays(parseISO(a.startTime), weekStart);
		if (dayIndex >= 0 && dayIndex < 7) {
			buckets[dayIndex].push(a);
		}
	});

	const loads: WeekLoad[] = [];
	perWorkerPerDay.forEach((buckets, workerId) => {
		const who = roster.get(workerId);
		if (!who) {
			return;
		}
		const dayHours = buckets.map(mergedHours);
		const all = buckets.flat();
		const weekCap = resolveCap(cap, workerId, all);
		const dayCaps = buckets.map((bucket, d) => {
			const resolver = dayCapResolvers?.[d];
			return resolver ? resolver(workerId, bucket) : weekCap / 5;
		});
		loads.push({
			workerId,
			name: who.name,
			role: who.role,
			dayHours,
			dayCaps,
			hours: dayHours.reduce((total, h) => total + h, 0),
			cap: weekCap,
			dayAssignments: buckets,
			dayUnavailable: buckets.map((bucket, d) => markerFor(availability, workerId, bucket, dayIsos[d])),
		});
	});

	return loads.sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name));
}

/**
 * The visible hour window. The mock assumes 05:00–14:00; real plans run
 * whenever the plant runs, so this follows the data and only falls back to the
 * mock's window when the day is empty.
 */
export function hourWindow(loads: WorkerLoad[]): { start: number; end: number } {
	const blocks = loads.flatMap((l) => l.blocks);
	if (!blocks.length) {
		return { start: 5, end: 14 };
	}
	const start = Math.floor(Math.min(...blocks.map((b) => b.start)));
	const end = Math.ceil(Math.max(...blocks.map((b) => b.end)));
	// Always leave at least a few hours of track so blocks don't fill the lane edge to edge.
	return { start, end: Math.max(end, start + 4) };
}

export interface BoardMetrics {
	utilPct: number
	utilSub: string
	coverPct: number
	coverSub: string
	overtimeHours: number
	overtimeHeads: number
	fairCount: number
	total: number
	spreadLabel: string
}

/**
 * Board totals. Every per-worker test uses THAT worker's cap (their shift
 * length), and "available" is the caps summed over the roster — not headcount
 * x 8, which called a plant of 8h 30m shifts over cap on every normal day.
 */
export function buildMetrics(
	loads: { hours: number; cap: number; outHours?: number }[],
	cap: number,
	orders: ManufacturingOrder[],
	requiredHours: number,
	fmt: (h: number) => string
): BoardMetrics {
	const scheduled = loads.reduce((total, l) => total + l.hours, 0);
	// Hours held by someone confirmed out are planned, not covered: the
	// coverage metric counts them as short so the headline agrees with the lines.
	const outHours = loads.reduce((total, l) => total + (l.outHours ?? 0), 0);
	const covered = Math.max(scheduled - outHours, 0);
	const available = loads.length ? loads.reduce((total, l) => total + l.cap, 0) : cap;
	const overtimeHours = loads.reduce((total, l) => total + Math.max(l.hours - l.cap, 0), 0);
	const overtimeHeads = loads.filter((l) => l.hours > l.cap).length;
	const fairCount = loads.filter((l) => l.hours <= l.cap && l.hours >= l.cap - 1.5).length;
	const sorted = [...loads].sort((a, b) => a.hours - b.hours);
	const cover = requiredHours > 0 ? Math.min(covered / requiredHours, 1) : 1;

	return {
		utilPct: available > 0 ? Math.round((scheduled / available) * 100) : 0,
		utilSub: `${fmt(scheduled)} scheduled of ${Math.round(available)}h available`,
		coverPct: Math.round(cover * 100),
		coverSub:
			requiredHours <= 0
				? "no labor standard on these orders"
				: covered >= requiredHours
					? `all ${orders.length} orders fully staffed`
					: `${fmt(requiredHours - covered)} short across ${orders.length} orders${outHours > 0 ? ` · ${fmt(outHours)} held by people out` : ""}`,
		overtimeHours,
		overtimeHeads,
		fairCount,
		total: loads.length,
		spreadLabel: sorted.length
			? `${fmt(sorted[0].hours)} → ${fmt(sorted[sorted.length - 1].hours)}`
			: "—",
	};
}

/**
 * A thing drawn in the hour lane: either one assignment, or a run of very short
 * ones collapsed together.
 */
export interface LaneItem {
	key: string
	start: number
	end: number
	label: string
	/** >1 means this is a cluster and is not individually selectable. */
	count: number
	members: BoardBlock[]
}

/** Below this share of the visible window a block cannot carry a readable label. */
const TINY_SHARE = 0.02;
/** Runs only collapse while the members are this close together. */
const TIGHT_GAP_SHARE = 0.015;

/**
 * Collapse tight runs of very short assignments into one block.
 *
 * A deferred inspection can be a single minute, and a worker can carry two
 * dozen of them; drawn individually they are a row of unlabelled slivers that
 * tells the planner nothing. A run becomes "12 × Technician III", and the
 * members stay on the tooltip. Blocks big enough to read are never touched, and
 * a run is only collapsed while its members are genuinely adjacent — otherwise
 * the cluster would span hours it does not occupy.
 */
export function clusterBlocks(blocks: BoardBlock[], span: number): LaneItem[] {
	const sorted = [...blocks].sort((a, b) => a.start - b.start);
	const items: LaneItem[] = [];
	let run: BoardBlock[] = [];

	const flush = () => {
		if (!run.length) {
			return;
		}
		if (run.length === 1) {
			const only = run[0];
			items.push({
				key: `b${only.id}`,
				start: only.start,
				end: only.end,
				label: only.label,
				count: 1,
				members: [only],
			});
		} else {
			const roles = Array.from(new Set(run.map((b) => b.jobRole).filter(Boolean)));
			items.push({
				key: `c${run[0].id}-${run.length}`,
				start: run[0].start,
				end: run[run.length - 1].end,
				label: `${run.length} × ${roles.length === 1 ? roles[0] : "tasks"}`,
				count: run.length,
				members: [...run],
			});
		}
		run = [];
	};

	sorted.forEach((block) => {
		const tiny = block.duration / span < TINY_SHARE;
		if (!tiny) {
			flush();
			items.push({
				key: `b${block.id}`,
				start: block.start,
				end: block.end,
				label: block.label,
				count: 1,
				members: [block],
			});
			return;
		}
		const previous = run[run.length - 1];
		if (previous && (block.start - previous.end) / span > TIGHT_GAP_SHARE) {
			flush();
		}
		run.push(block);
	});
	flush();
	return items;
}
